import { getApiBase } from '../apiBase.js';
import { enrollmentFailure } from './phoneEnrollmentErrors.js';
import { willReloadAfterWorkerChange } from './appUpdates.js';

const CLEANUP_KEY = 'tutory:inbox-push-cleanup:v1';
const SUSPENDED_KEY = 'tutory:inbox-push-suspended:v1';
const ORPHAN_KEY = 'tutory:inbox-push-orphan:v1';
const LOCK = 'tutory:inbox-push-device:v1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const fail = code => Object.assign(new Error(code), { code });
const tokenNow = () => localStorage.getItem('jwtToken');

function publicBinding(value) {
  if (!value || !UUID.test(value.id) || !UUID.test(value.generation) ||
      !Number.isSafeInteger(value.owner_id) || value.owner_id <= 0 || value.revoked !== false ||
      (value.expires_at !== null && !(typeof value.expires_at === 'string' && Number.isFinite(Date.parse(value.expires_at)))))
    throw fail('push_binding_invalid');
  return { id: value.id, generation: value.generation, owner_id: value.owner_id,
    expires_at: value.expires_at, revoked: false };
}

export function phoneWorkerCommand(type, data = {}) {
  return new Promise((resolve, reject) => {
    const worker = navigator.serviceWorker?.controller;
    if (!worker || worker.scriptURL !== location.origin + '/service-worker.js') {
      reject(fail('push_worker_unavailable')); return;
    }
    const channel = new MessageChannel();
    const finish = (error, value) => {
      clearTimeout(timer); channel.port1.close();
      if (error) reject(error); else resolve(value);
    };
    const timer = setTimeout(() => finish(fail('push_worker_unavailable')), 2000);
    channel.port1.onmessage = event => {
      const result = event.data;
      if (!result?.ok || !UUID.test(result.revision)) finish(fail(result?.error || 'push_worker_unavailable'));
      else finish(null, result);
    };
    try { worker.postMessage({ version: 1, type: `tutory:push-${type}`, ...data }, [channel.port2]); }
    catch { channel.port2.close(); finish(fail('push_worker_unavailable')); }
  });
}

function withDeviceLock(work) {
  if (!navigator.locks?.request) return Promise.reject(fail('push_lock_unavailable'));
  return navigator.locks.request(LOCK, { mode: 'exclusive' }, work);
}

function cleanupQueue() {
  const raw = localStorage.getItem(CLEANUP_KEY);
  if (!raw) return [];
  let values;
  try { values = JSON.parse(raw); } catch { throw fail('push_cleanup_unavailable'); }
  if (!Array.isArray(values) || values.length > 20) throw fail('push_cleanup_unavailable');
  return values.map(item => ({ ...publicBinding(item), unsubscribe: item.unsubscribe === true }));
}

function saveQueue(values) {
  if (values.length > 20) throw fail('push_cleanup_unavailable');
  // Explicit write even when empty: inability to retain cleanup blocks enrollment.
  localStorage.setItem(CLEANUP_KEY, JSON.stringify(values));
}

function rememberCleanup(binding, unsubscribe = true) {
  const clean = publicBinding(binding), queue = cleanupQueue();
  const index = queue.findIndex(item => item.id === clean.id && item.generation === clean.generation);
  if (index < 0) queue.push({ ...clean, unsubscribe });
  else queue[index].unsubscribe ||= unsubscribe;
  saveQueue(queue);
}

async function registration() {
  const result = await navigator.serviceWorker.getRegistration('/');
  if (!result || result.scope !== location.origin + '/' ||
      result.active?.scriptURL !== location.origin + '/service-worker.js' || !result.pushManager)
    throw fail('push_worker_unavailable');
  return result;
}

async function revoke(binding, token) {
  if (!token) return false;
  const abort = new AbortController(), timer = setTimeout(() => abort.abort(), 8000);
  try {
    const response = await fetch(`${getApiBase()}/studentplayground/api/inbox/push/subscriptions/${binding.id}/revoke/`, {
      method: 'POST', cache: 'no-store', credentials: 'same-origin', signal: abort.signal,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ expected_generation: binding.generation }),
    });
    if (!response.ok) return false;
    const result = await response.json();
    return result.id === binding.id && result.generation === binding.generation &&
      result.owner_id === binding.owner_id && result.revoked === true;
  } catch { return false; }
  finally { clearTimeout(timer); }
}

async function drainCleanup(ownerId, token) {
  let queue = cleanupQueue();
  const orphanRaw = localStorage.getItem(ORPHAN_KEY);
  if (!queue.length && !orphanRaw) return true;
  const state = await phoneWorkerCommand('state');
  if (orphanRaw) {
    let orphan;
    try { orphan = JSON.parse(orphanRaw); } catch { throw fail('push_cleanup_unavailable'); }
    if (!UUID.test(orphan?.revision)) throw fail('push_cleanup_unavailable');
    // Unknown registration outcomes have no server id to revoke. Retain only
    // the empty-worker revision, never the endpoint, keys or credential.
    if (!state.binding && state.revision === orphan.revision) {
      const existing = await (await registration()).pushManager.getSubscription();
      if (existing && !(await existing.unsubscribe())) return false;
    }
    // A different revision belongs to a later operation: it is never ours to
    // unsubscribe, even when that later operation has since cleared its binding.
    localStorage.removeItem(ORPHAN_KEY);
  }
  // Never unsubscribe a subscription now owned by another/newer binding.
  if (!state.binding && queue.some(item => item.unsubscribe)) {
    const existing = await (await registration()).pushManager.getSubscription();
    if (existing && !(await existing.unsubscribe())) return false;
    queue = queue.map(item => ({ ...item, unsubscribe: false }));
    saveQueue(queue);
  }
  for (const item of [...queue]) {
    if (item.owner_id === ownerId && await revoke(item, token)) {
      queue = queue.filter(value => value.id !== item.id || value.generation !== item.generation);
      saveQueue(queue);
    }
  }
  return !queue.some(item => item.owner_id === ownerId);
}

export function retryPhoneCleanup(ownerId, token = tokenNow()) {
  return withDeviceLock(() => drainCleanup(ownerId, token));
}

export function disconnectPhone(ownerId, token = tokenNow()) {
  return withDeviceLock(async () => {
    const state = await phoneWorkerCommand('state');
    if (tokenNow() !== null && tokenNow() !== token) throw fail('push_session_changed');
    if (state.binding) {
      if (state.binding.owner_id !== ownerId) throw fail('push_binding_conflict');
      // Retain exact-generation work before clearing the worker or doing I/O.
      let storageError;
      try { rememberCleanup(state.binding); } catch (problem) { storageError = problem; }
      await phoneWorkerCommand('clear', { expected_revision: state.revision });
      if (storageError) throw storageError;
    }
    return drainCleanup(ownerId, token);
  });
}

function decodePublicKey(value) {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]{87}$/.test(value)) throw fail('push_configuration_invalid');
  const bytes = Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/') + '='), char => char.charCodeAt(0));
  if (bytes.length !== 65 || bytes[0] !== 4) throw fail('push_configuration_invalid');
  return bytes;
}

export function phoneCanEnroll(config, ownerId) {
  return config?.enabled === true && config.configuration === 'valid' && config.user_id === ownerId &&
    config.origin_matches === true && config.inbox_url === location.origin + '/studentplayground/inbox/#/inbox' &&
    typeof config.vapid_key_id === 'string' && !!config.vapid_key_id && typeof config.public_key === 'string';
}

// Called directly by the click handler. OS permission is requested before any
// await/lock/network operation so iOS retains the deliberate user activation.
export function enrollPhone({ api, config, ownerId, current = () => true }) {
  let stage = 'preflight';
  try {
    if (!phoneCanEnroll(config, ownerId) || location.pathname !== '/studentplayground/inbox/' || location.search ||
      !(matchMedia('(display-mode: standalone)').matches || navigator.standalone === true) ||
      !window.isSecureContext || !navigator.locks?.request) throw fail('push_not_ready');
    stage = 'key';
    const key = decodePublicKey(config.public_key);
    stage = 'storage';
    const token = tokenNow();
    saveQueue(cleanupQueue());
    const check = () => {
      if (!token || tokenNow() !== token || !current()) {
        stage = 'session';
        throw fail('push_session_changed');
      }
    };
    check();
    stage = 'permission';
    // Keep the OS prompt synchronous with the click; diagnostics must not add
    // an await, lock, telemetry request or retry before user activation.
    const permission = Notification.permission === 'default' ? Notification.requestPermission() : Notification.permission;
    return Promise.resolve(permission).then(granted => {
    if (granted !== 'granted') throw fail('push_permission_denied');
    stage = 'lock';
    return withDeviceLock(async () => {
      check();
      stage = 'cleanup';
      if (!(await drainCleanup(ownerId, token))) throw fail('push_cleanup_pending');
      check();
      stage = 'config-read';
      const verified = await api('push/', { timeoutMs: 10000 });
      check();
      stage = 'config-check';
      if (!phoneCanEnroll(verified, ownerId) || verified.vapid_key_id !== config.vapid_key_id ||
          verified.public_key !== config.public_key) throw fail('push_configuration_changed');
      stage = 'worker-state';
      const state = await phoneWorkerCommand('state');
      check();
      if (state.binding && state.binding.owner_id !== ownerId) throw fail('push_binding_conflict');
      stage = 'registration';
      const manager = (await registration()).pushManager;
      check();
      stage = 'subscription-read';
      let subscription = await manager.getSubscription();
      check();
      stage = 'key-check';
      if (subscription?.options?.applicationServerKey) {
        const priorKey = new Uint8Array(subscription.options.applicationServerKey);
        if (priorKey.length !== key.length || priorKey.some((value, index) => value !== key[index]))
          throw fail('push_key_conflict');
      }
      let bound;
      try {
        stage = 'pending-store';
        if (!state.binding) localStorage.setItem(ORPHAN_KEY, JSON.stringify({ revision: state.revision }));
        stage = 'subscribe';
        subscription ||= await manager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        check();
        stage = 'subscription-json';
        const subscriptionData = subscription.toJSON();
        stage = 'server-register';
        const result = await api('push/subscriptions/', { method: 'POST', timeoutMs: 10000, body: {
          subscription: subscriptionData, vapid_key_id: config.vapid_key_id,
          ...(state.binding ? { expected_generation: state.binding.generation } : {}),
        } });
        stage = 'binding-check';
        bound = publicBinding(result);
        if (bound.owner_id !== ownerId || (bound.expires_at && Date.parse(bound.expires_at) <= Date.now())) throw fail('push_binding_invalid');
        // A response arriving after logout is still queued for exact revocation.
        stage = 'cleanup-store';
        rememberCleanup(bound);
        localStorage.removeItem(ORPHAN_KEY);
        check();
        stage = 'worker-bind';
        await phoneWorkerCommand('bind', { expected_revision: state.revision, binding: bound });
        check();
        stage = 'cleanup-finish';
        saveQueue(cleanupQueue().filter(item => item.id !== bound.id || item.generation !== bound.generation));
        return bound;
      } catch (problem) {
        const latest = await phoneWorkerCommand('state').catch(() => null);
        if (latest && (!latest.binding || (bound && latest.binding.id === bound.id &&
            latest.binding.generation === bound.generation))) {
          if (latest.binding) await phoneWorkerCommand('clear', { expected_revision: latest.revision }).catch(() => {});
          // The shared device lock prevents another cooperating tab from
          // subscribing while this attempt unsubscribes its captured endpoint.
          const removed = !subscription || await subscription.unsubscribe().catch(() => false);
          if (removed) localStorage.removeItem(ORPHAN_KEY);
        }
        if (bound?.owner_id === ownerId) await drainCleanup(ownerId, token).catch(() => {});
        throw problem;
      }
    });
    }).catch(problem => { throw enrollmentFailure(problem, stage); });
  } catch (problem) {
    return Promise.reject(enrollmentFailure(problem, stage));
  }
}

async function authenticatedPhoneRead(path, token) {
  const abort = new AbortController(), timer = setTimeout(() => abort.abort(), 8000);
  try {
    const response = await fetch(`${getApiBase()}/studentplayground/api/inbox/${path}`, {
      cache: 'no-store', credentials: 'same-origin', signal: abort.signal,
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw fail('push_identity_unverified');
    return await response.json();
  } finally { clearTimeout(timer); }
}

// One app-lifetime observer, including pages outside the inbox. Token changes
// suspend private previews first; JWT expiry/renewal alone does not revoke a
// subscription. Only authenticated identity can authorize restoring ownership.
export function installPhoneLifecycle() {
  if (!navigator.serviceWorker || !navigator.locks?.request) return () => {};
  let observedToken, epoch = 0, alive = true;
  try { observedToken = tokenNow(); } catch { observedToken = null; }
  const storageUnavailable = () => {
    try { tokenNow(); return false; } catch { return true; }
  };
  async function redactUnreadableSession() {
    try {
      await withDeviceLock(async () => {
        if (!alive || !storageUnavailable()) return;
        const state = await phoneWorkerCommand('state');
        if (alive && storageUnavailable() && state.binding)
          await phoneWorkerCommand('clear', { expected_revision: state.revision });
      });
    } catch { /* No storage or credential can authorize restoration or revocation. */ }
  }
  async function reconcile({ logout = false, force = false, recovery = false, oldToken = observedToken } = {}) {
    let nextToken;
    try { nextToken = tokenNow(); } catch { ++epoch; await redactUnreadableSession(); return; }
    if (!force && !logout && nextToken === observedToken) return;
    observedToken = nextToken;
    const run = ++epoch;
    const current = () => alive && run === epoch && tokenNow() === nextToken;
    try {
      const suspended = await withDeviceLock(async () => {
        if (!current()) return null;
        const state = await phoneWorkerCommand('state');
        if (!current()) return null;
        // A reconnect is a retry of recorded work, not authority to suspend or
        // unsubscribe a binding another tab has installed in the meantime.
        if (recovery && state.binding) return null;
        if (state.binding) {
          const binding = publicBinding(state.binding);
          // Clear even if storage is unavailable; private previews fail closed.
          let stored = true;
          try { localStorage.setItem(SUSPENDED_KEY, JSON.stringify({ binding, revision: state.revision })); }
          catch { stored = false; }
          const cleared = await phoneWorkerCommand('clear', { expected_revision: state.revision });
          const value = { binding, revision: cleared.revision };
          if (stored) localStorage.setItem(SUSPENDED_KEY, JSON.stringify(value));
          return value;
        }
        const raw = localStorage.getItem(SUSPENDED_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        return { binding: publicBinding(saved.binding), revision: saved.revision };
      });
      if (!current()) return;
      if (!suspended) {
        if (logout) {
          // An interrupted first enrollment may have no server binding yet.
          await retryPhoneCleanup(null, null);
        } else if (nextToken && (cleanupQueue().length || localStorage.getItem(ORPHAN_KEY))) {
          const identity = await authenticatedPhoneRead('push/', nextToken);
          if (alive && run === epoch && tokenNow() === nextToken)
            await retryPhoneCleanup(identity.user_id, nextToken);
        }
        return;
      }
      if (logout) {
        await withDeviceLock(async () => {
          if (!current()) return;
          rememberCleanup(suspended.binding);
          await drainCleanup(suspended.binding.owner_id, oldToken);
          if (localStorage.getItem(SUSPENDED_KEY) === JSON.stringify(suspended)) localStorage.removeItem(SUSPENDED_KEY);
        });
        return;
      }
      if (!nextToken) return; // No credential is not proof of membership revocation.
      const identity = await authenticatedPhoneRead('push/', nextToken);
      if (!alive || run !== epoch || tokenNow() !== nextToken) return;
      if (identity.user_id !== suspended.binding.owner_id) {
        await withDeviceLock(async () => {
          if (!current()) return;
          rememberCleanup(suspended.binding);
          await drainCleanup(suspended.binding.owner_id, oldToken);
          if (localStorage.getItem(SUSPENDED_KEY) === JSON.stringify(suspended)) localStorage.removeItem(SUSPENDED_KEY);
        });
        return;
      }
      const verified = await authenticatedPhoneRead(`push/subscriptions/${suspended.binding.id}/`, nextToken);
      if (!alive || run !== epoch || tokenNow() !== nextToken ||
          verified.id !== suspended.binding.id || verified.generation !== suspended.binding.generation ||
          verified.owner_id !== identity.user_id || verified.revoked !== false || verified.eligible !== true) return;
      // The student entry cannot publish a binding. Resume in the dedicated
      // inbox after fresh authorization, never by trusting browser JWT claims.
      if (location.pathname !== '/studentplayground/inbox/' || location.search) return;
      await withDeviceLock(async () => {
        if (!alive || run !== epoch || tokenNow() !== nextToken) return;
        const state = await phoneWorkerCommand('state');
        if (state.binding || state.revision !== suspended.revision) return;
        await phoneWorkerCommand('bind', { binding: publicBinding(verified), expected_revision: state.revision });
        if (localStorage.getItem(SUSPENDED_KEY) === JSON.stringify(suspended)) localStorage.removeItem(SUSPENDED_KEY);
      });
    } catch {
      // Storage/network/ownership uncertainty must never restore private data.
      // Durable nonsecret records allow a later authenticated reconnect to retry.
    }
  }
  const logout = () => { void reconcile({ logout: true, force: true }); };
  const storage = event => {
    // Storage events can be delivered after a newer write. Act on the current
    // credential, not a queued event describing a previous login/logout.
    try {
      if (event.storageArea && event.storageArea !== localStorage) return;
      if (event.key === 'jwtToken' && event.newValue !== tokenNow()) return;
      if (event.key === null && tokenNow() !== null) return;
    } catch { void redactUnreadableSession(); return; }
    if (event.key === null || (event.key === 'jwtToken' && event.newValue === null))
      void reconcile({ logout: true, force: true, oldToken: event.oldValue || observedToken });
    else if (event.key === 'jwtToken') void reconcile({ oldToken: event.oldValue || observedToken });
  };
  const changed = () => { void reconcile({ force: true }); };
  const focused = () => { void reconcile(); };
  const reopened = () => { void reconcile({ force: true }); };
  const controllerChanged = () => {
    // A committed update is about to discard this context. Starting a clear
    // here can commit in the worker after this page loses the reply needed to
    // save its new revision. The replacement page revalidates on cold start.
    // Unplanned activation still revalidates here; logout/session changes are
    // never suppressed by the update coordinator.
    if (!willReloadAfterWorkerChange()) reopened();
  };
  const pageShown = event => { if (event.persisted) reopened(); };
  const reconnect = () => {
    try { void reconcile({ force: true, recovery: tokenNow() === observedToken }); }
    catch { void redactUnreadableSession(); }
  };
  window.addEventListener('tutory:logout', logout);
  window.addEventListener('tutory:session-changed', changed);
  window.addEventListener('storage', storage);
  window.addEventListener('focus', focused);
  window.addEventListener('online', reconnect);
  window.addEventListener('pageshow', pageShown);
  navigator.serviceWorker.addEventListener('controllerchange', controllerChanged);
  // Every newly opened context revalidates existing ownership. No OS prompt,
  // subscription or decoded JWT claim can establish that ownership.
  reopened();
  return () => {
    alive = false; ++epoch;
    window.removeEventListener('tutory:logout', logout);
    window.removeEventListener('tutory:session-changed', changed);
    window.removeEventListener('storage', storage);
    window.removeEventListener('focus', focused);
    window.removeEventListener('online', reconnect);
    window.removeEventListener('pageshow', pageShown);
    navigator.serviceWorker.removeEventListener('controllerchange', controllerChanged);
  };
}
